<template>
    <div
        ref="dListWrapperRef"
    >
        <div
            ref="dListRef"
            class="flex md:inline-flex items-center"
        >
            <div
                v-for="(item, index) in shortList"
                :key="index"
                :ref="el => setItemRef(el, index)"
                class="flex items-center"
                :title="getTooltip(item)"
            >
                <slot
                    name="list-item"
                    :item="item"
                    :index="index"
                >
                    {{ getValueKey(item, index) }}
                </slot>
                <span
                    v-if="showSeparator(index)"
                    class="text-break"
                >
                    <slot name="separator">{{ separator }}</slot>
                </span>
            </div>
            <template v-if="otherList.length">
                <VcPopover
                    trigger="hover"
                    placement="top"
                >
                    <template #reference>
                        <div>
                            <slot
                                name="other-content"
                                :list="otherList"
                            >
                                {{ otherList.length }}
                            </slot>
                        </div>
                    </template>
                    <slot
                        name="other-tooltip-content"
                        :list="otherList"
                    >
                        {{ getTooltip(otherList) }}
                    </slot>
                </VcPopover>
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { VcPopover } from '@voicenter-team/voicenter-ui-plus'

interface Item {
    [key: string]: unknown
}

const props = withDefaults(defineProps<{
    items: Item[]
    separator?: string
    popoverMaxWidth?: string
    popoverDisabled?: boolean
    valueKey?: string
    visibleCounts?: number
    containerWidth?: number
}>(), {
    separator: ', ',
    popoverMaxWidth: '300px',
    popoverDisabled: false,
    visibleCounts: 0,
    containerWidth: 300
})

const dListWrapperRef = ref<HTMLElement | null>(null)
const dListRef = ref<HTMLElement | null>(null)
const itemRefs = ref<HTMLElement[]>([])

const separator = props.separator ?? ','

const visibleCount = ref(props.items.length)

const shortList = computed(() => props.items.slice(0, visibleCount.value))
const otherList = computed(() => props.items.slice(visibleCount.value))

function showSeparator (index: number) {
    return index < shortList.value.length - 1
}

function getValueKey (item: Item, index: number) {
    if (props.valueKey && item && item[props.valueKey] !== undefined) {
        return item[props.valueKey]
    }
    return index
}

function getTooltip (item: Item[] | Item) {
    return item && typeof item === 'object' ? JSON.stringify(item) : String(item ?? '')
}

const setItemRef = (el: HTMLElement | null, index: number) => {
    if (el) {
        itemRefs.value[index] = el
    }
}
const calculateVisibleCount = async () => {
    await nextTick()

    if (!props.containerWidth) return

    let totalWidth = 30
    let count = 0

    for (const el of itemRefs.value) {
        if (!el) continue
        const itemWidth = el.clientWidth + 8
        if (totalWidth + itemWidth > props.containerWidth) break
        totalWidth += itemWidth
        count++
    }

    visibleCount.value = count
}

watch(() => props.containerWidth, () => {
    visibleCount.value = props.items.length
    setTimeout(() => {
        calculateVisibleCount()
    }, 500)
})

onMounted(() => {
    calculateVisibleCount()
})

</script>

